/*
 * game-wallet.js — troca "Moedas <-> Blue Coins" dentro das webviews de jogo.
 *
 * FONTE ÚNICA: app/games/_tools/game-wallet/game-wallet.js
 * Cada jogo carrega uma CÓPIA IDÊNTICA (os jogos são repos separados, com deploy
 * próprio). Mudou aqui → rode `node app/games/_tools/game-wallet/sync.mjs`.
 *
 * "Blue Coins" é o nome, na tela, das moedas de jogo (users.game_coins).
 *
 * Uso:
 *   const w = await GameWallet.init({ apiUrl, token, theme: { accent: '#f8d26a' } })
 *   // w = { coins, gameCoins, enabled } — enabled=false: nada aparece, o jogo fica como antes
 *   const pill = GameWallet.createPill() // botão padrão: moeda azul + Blue Coins + "+"
 *   pill.setValue(n)                    // mostra n (ex.: saldo otimista do jogo)
 *   GameWallet.open('in' | 'out')       // abre o modal
 *   GameWallet.refresh()                // relê o saldo no servidor
 *   GameWallet.onChange(fn)             // fn({ coins, gameCoins, enabled }) a cada mudança
 *   GameWallet.openConsent()            // pede ao app pra abrir o Jogo Responsável
 *   GameWallet.consentAsk(code)         // modal grande "Ative o Jogo Responsável" (Sim → openConsent)
 *   GameWallet.isConsentError(code)     // consent_required / daily_limit_*
 *   GameWallet.coinSvg(size)            // SVG da moeda azul (string)
 * Também dispara window 'game-wallet-changed' (detail = carteira).
 */
(function (root) {
  'use strict'
  if (root.GameWallet) return

  var NAME = 'Blue Coins'
  var INT_MAX = 2147483647 // os valores das mutations são Int (32 bits)
  var QUICK = [1000, 10000, 100000]
  var CONSENT_CODES = ['consent_required', 'daily_limit_not_set', 'daily_limit_reached']

  var cfg = { apiUrl: '', token: '', theme: {} }
  var BLUE_THEME = {
    bg: 'linear-gradient(180deg, #0d3b66, #061a30)',
    surface: 'rgba(120, 200, 255, 0.08)',
    input: 'rgba(0, 0, 0, 0.35)',
    line: 'rgba(120, 200, 255, 0.35)',
    text: '#eaf6ff',
    muted: 'rgba(220, 238, 255, 0.7)',
    accent: '#4aa8ff',
    accentInk: '#03203d',
  }
  // explicação no topo do modal (open(m, reason)); hoje os jogos usam o aviso ask()
  var INTRO = {
    start: 'Para jogar, é preciso converter suas moedas em ' + 'Blue Coins. Deseja converter para jogar?',
    empty: 'Seus ' + 'Blue Coins acabaram. Para continuar jogando, converta moedas em ' + 'Blue Coins.',
  }
  // aviso ANTES da carteira (ask): entrou sem Blue Coins / acabaram no jogo
  var ASK = {
    start: 'Este jogo é jogado com ' + 'Blue Coins e você não possui. Deseja converter para jogar?',
    empty: 'Seus ' + 'Blue Coins acabaram. Deseja converter para continuar jogando?',
  }
  // modal do Jogo Responsável (consentAsk): título grande + explicação
  var CONSENT_ASK = {
    consent_required: ['Ative o Jogo Responsável', 'Para jogar com ' + 'Blue Coins, você precisa aceitar o termo de Jogo Responsável.'],
    daily_limit_not_set: ['Defina seu limite do dia', 'Para jogar com ' + 'Blue Coins, escolha no Jogo Responsável quanto quer gastar por dia.'],
    daily_limit_reached: ['Limite de hoje atingido', 'Você chegou no limite que escolheu para hoje. Você pode rever o limite no Jogo Responsável.'],
  }
  var state = { coins: 0, gameCoins: 0, enabled: false, loaded: false }
  var listeners = []
  var pills = []
  var ui = null
  var mode = 'in'
  var busy = false
  var autoOpened = false
  var svgSeq = 0

  function fmt(n) {
    return Math.max(0, Math.floor(Number(n) || 0))
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, '.')
  }
  // formato da pílula: completo até 7 dígitos (9.999.999); a partir de 8, 1 casa em pt-BR (10,0 mi / 2,4 bi).
  // Arredonda pra BAIXO (nunca mostra mais do que a pessoa tem).
  function fmtShort(n) {
    var v = Math.max(0, Math.floor(Number(n) || 0))
    if (v <= 9999999) return fmt(v)
    var units = [
      [1e12, 'tri'],
      [1e9, 'bi'],
      [1e6, 'mi'],
    ]
    for (var i = 0; i < units.length; i++) {
      if (v >= units[i][0]) {
        var d = Math.floor((v / units[i][0]) * 10) / 10
        return fmt(Math.floor(d)) + ',' + Math.round((d % 1) * 10) + ' ' + units[i][1]
      }
    }
    return fmt(v)
  }
  function parse(s) {
    var d = String(s || '').replace(/\D/g, '')
    return d ? Number(d) : 0
  }
  function uuid() {
    try {
      if (root.crypto && root.crypto.randomUUID) return root.crypto.randomUUID()
    } catch (e) {}
    return 'gw-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12)
  }

  // moeda azul: mesma forma da moeda dourada do app (borda grossa, miolo rebaixado, letra)
  function coinSvg(size) {
    var s = size || 24
    var id = 'gwc' + ++svgSeq
    return (
      '<svg class="gw-coin" width="' + s + '" height="' + s + '" viewBox="0 0 64 64" aria-hidden="true" focusable="false">' +
      '<defs>' +
      '<linearGradient id="' + id + 'e" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#1f63db"/><stop offset="1" stop-color="#0a2a78"/></linearGradient>' +
      '<radialGradient id="' + id + 'f" cx=".36" cy=".28" r=".85"><stop offset="0" stop-color="#bfe6ff"/><stop offset=".45" stop-color="#45a0ff"/><stop offset="1" stop-color="#1550c8"/></radialGradient>' +
      '<linearGradient id="' + id + 'i" x1="0" y1="1" x2="0" y2="0"><stop offset="0" stop-color="#6ebcff"/><stop offset="1" stop-color="#2a78ea"/></linearGradient>' +
      '</defs>' +
      '<circle cx="32" cy="35" r="27" fill="url(#' + id + 'e)"/>' +
      '<circle cx="32" cy="30" r="27" fill="url(#' + id + 'f)"/>' +
      '<circle cx="32" cy="30" r="18.5" fill="url(#' + id + 'i)" stroke="#1448b0" stroke-opacity=".55" stroke-width="2"/>' +
      '<text x="32" y="38.6" text-anchor="middle" font-family="Arial,Helvetica,sans-serif" font-size="23" font-weight="900" fill="#eef8ff" stroke="#1448b0" stroke-width="1.2" paint-order="stroke">B</text>' +
      '<ellipse cx="22" cy="15" rx="9" ry="3.6" fill="#fff" opacity=".38" transform="rotate(-28 22 15)"/>' +
      '</svg>'
    )
  }

  // ---------------------------------------------------------------- servidor
  function gql(query, variables) {
    return fetch(cfg.apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + cfg.token },
      body: JSON.stringify({ query: query, variables: variables || {} }),
    })
      .then(function (r) {
        return r.json().catch(function () {
          return null
        })
      })
      .then(function (j) {
        var env = j && j.data ? j.data[Object.keys(j.data)[0]] : null
        if (!env) throw new Error((j && j.errors && j.errors[0] && j.errors[0].message) || 'network')
        if (env.status === 'error' || env.error) throw new Error(env.error || 'error')
        return env.data
      })
  }
  var Q_WALLET = 'query getGameWallet { getGameWallet { error status data } }'
  var M_IN =
    'mutation convertCoinsToGameCoins($amount: Int!, $requestId: String!) { convertCoinsToGameCoins(amount: $amount, requestId: $requestId) { error status data } }'
  var M_OUT =
    'mutation convertGameCoinsToCoins($amount: Int!, $requestId: String!) { convertGameCoinsToCoins(amount: $amount, requestId: $requestId) { error status data } }'

  function apply(d) {
    if (!d) return
    if (typeof d.coins === 'number') state.coins = d.coins
    if (typeof d.gameCoins === 'number') state.gameCoins = d.gameCoins
    if (typeof d.enabled === 'boolean') state.enabled = d.enabled
    state.loaded = true
    var snap = snapshot()
    pills.forEach(function (p) {
      p.setValue(snap.gameCoins)
    })
    listeners.forEach(function (fn) {
      try {
        fn(snap)
      } catch (e) {}
    })
    try {
      root.dispatchEvent(new CustomEvent('game-wallet-changed', { detail: snap }))
    } catch (e) {}
    paint()
  }
  function snapshot() {
    return { coins: state.coins, gameCoins: state.gameCoins, enabled: state.enabled }
  }

  function refresh() {
    return gql(Q_WALLET).then(function (d) {
      apply(d)
      return snapshot()
    })
  }

  // ---------------------------------------------------------------- app (RN / iframe)
  function post(msg) {
    try {
      if (root.ReactNativeWebView) return root.ReactNativeWebView.postMessage(msg)
      if (root.parent && root.parent !== root) return root.parent.postMessage(msg, '*')
    } catch (e) {}
  }
  // Dentro do hub (iframe): o hub abre o termo por cima. Direto no app (jogo da live): o app
  // da loja ignora 'open-consent-game', então o próprio widget abre o termo numa camada.
  function openConsent() {
    if (root.parent && root.parent !== root) return post('open-consent-game')
    if (!cfg.token) return post('open-consent-game')
    openConsentLayer()
  }
  var consentLayer = null
  function consentUrl() {
    var dev = /-dev\./.test(cfg.apiUrl || '') || /-dev/.test(root.location.hostname)
    return (dev ? 'https://consent-game-dev.didderlive.com' : 'https://consent-game.didderlive.com') +
      '/?embed=1&token=' + encodeURIComponent(cfg.token)
  }
  function openConsentLayer() {
    injectCss()
    if (consentLayer && consentLayer.parentNode) return
    consentLayer = el('div', 'gw-consent-layer')
    consentLayer.setAttribute('data-testid', 'gw-consent-layer')
    var fr = document.createElement('iframe')
    fr.src = consentUrl()
    fr.title = 'Jogo Responsável'
    consentLayer.appendChild(fr)
    document.body.appendChild(consentLayer)
  }
  function closeConsentLayer() {
    if (consentLayer && consentLayer.parentNode) consentLayer.parentNode.removeChild(consentLayer)
    consentLayer = null
    refresh().catch(function () {})
  }
  root.addEventListener('message', function (e) {
    if (!consentLayer) return
    var d = e && e.data
    if (d === 'close-game' || d === 'close' || d === 'close-game-now' || d === 'native-back') closeConsentLayer()
  })
  // "Não" no aviso: sai do jogo na hora (sem o modal de confirmação do hub).
  // Mesma ordem do botão de sair do jogo: iframe do hub primeiro, depois RN.
  function exitGame() {
    try {
      if (root.parent && root.parent !== root) return root.parent.postMessage('close-game-now', '*')
      if (root.ReactNativeWebView) return root.ReactNativeWebView.postMessage('close-game-now')
      root.history.back()
    } catch (e) {}
  }
  function isConsentError(code) {
    return CONSENT_CODES.indexOf(String(code || '')) !== -1
  }

  // ---------------------------------------------------------------- textos
  function errorText(code) {
    switch (code) {
      case 'insufficient_balance':
        return mode === 'in'
          ? 'Você não tem moedas suficientes para essa troca.'
          : 'Você não tem ' + NAME + ' suficientes para essa troca.'
      case 'consent_required':
        return 'Para colocar moedas em ' + NAME + ', aceite o termo de Jogo Responsável.'
      case 'daily_limit_not_set':
        return 'Defina seu limite do dia no Jogo Responsável para colocar moedas em ' + NAME + '.'
      case 'daily_limit_reached':
        return 'Você chegou no seu limite de hoje. Volte amanhã.'
      case 'game_wallet_off':
        return 'A troca está desligada agora. Tente mais tarde.'
      case 'invalid_value':
        return 'Digite um valor válido.'
      case 'not_authorized':
        return 'Sua sessão expirou. Feche e abra o jogo de novo.'
      case 'network':
        return 'Sem conexão. Tente de novo.'
      default:
        return 'Não foi possível trocar agora. Tente de novo.'
    }
  }
  function emptyText(m) {
    return m === 'in' ? 'Você não tem moedas' : 'Você não tem ' + NAME
  }

  // ---------------------------------------------------------------- CSS
  var CSS =
    // pílula padrão (igual em todos os jogos)
    '.gw-pill{display:inline-flex;align-items:center;gap:5px;height:36px;max-width:100%;padding:0 4px 0 4px;margin:0;border-radius:999px;border:1.5px solid rgba(125,192,255,.8);background:linear-gradient(180deg,rgba(10,36,96,.92),rgba(4,16,50,.94));color:#eef7ff;font:800 15px/1 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;font-variant-numeric:tabular-nums;box-shadow:0 2px 8px rgba(0,0,0,.35),inset 0 1px 0 rgba(255,255,255,.14);cursor:pointer;-webkit-tap-highlight-color:transparent;box-sizing:border-box;vertical-align:middle}' +
    '.gw-pill:active{transform:scale(.96)}' +
    '.gw-pill .gw-coin{flex:none;display:block}' +
    '.gw-pill-val{white-space:nowrap;padding:0 1px}' +
    '.gw-pill-plus{flex:none;width:22px;height:22px;border-radius:50%;background:#3d97ff;color:#fff;display:grid;place-items:center;font-size:18px;font-weight:900;line-height:1;box-shadow:inset 0 -2px 0 rgba(0,0,0,.2)}' +
    // modal
    '.gw-overlay{position:fixed;inset:0;z-index:2147483000;pointer-events:auto;display:flex;align-items:flex-end;justify-content:center;background:rgba(0,0,0,.55);font-family:var(--gw-font);-webkit-tap-highlight-color:transparent;opacity:0;transition:opacity .18s ease}' +
    '.gw-overlay.gw-on{opacity:1}' +
    '.gw-sheet{box-sizing:border-box;width:100%;max-width:420px;max-height:calc(100dvh - 24px);overflow-y:auto;background:var(--gw-bg);color:var(--gw-text);border-radius:22px 22px 0 0;border:1px solid var(--gw-line);border-bottom:0;padding:18px 16px calc(16px + env(safe-area-inset-bottom));box-shadow:0 -12px 40px rgba(0,0,0,.5);transform:translateY(24px);transition:transform .2s ease;-webkit-user-select:none;user-select:none}' +
    '.gw-on .gw-sheet{transform:none}' +
    '@media (min-height:700px) and (min-width:500px){.gw-overlay{align-items:center}.gw-sheet{border-radius:22px;border-bottom:1px solid var(--gw-line)}}' +
    '.gw-sheet *{box-sizing:border-box;font-family:inherit}' +
    '.gw-head{display:flex;align-items:center;justify-content:space-between;margin-bottom:12px}' +
    '.gw-title{font-size:19px;font-weight:800;margin:0;letter-spacing:.01em}' +
    '.gw-x{width:36px;height:36px;border-radius:50%;border:1px solid var(--gw-line);background:var(--gw-surface);color:var(--gw-text);font-size:18px;line-height:1;cursor:pointer;display:grid;place-items:center}' +
    '.gw-intro{margin:0 0 12px;padding:11px 12px;border-radius:14px;background:rgba(74,168,255,.16);border:1px solid rgba(110,180,255,.55);color:#eaf6ff;font-size:15px;font-weight:700;line-height:1.35}' +
    '.gw-intro[hidden]{display:none}' +
    '.gw-ask{box-sizing:border-box;width:calc(100% - 32px);max-width:360px;margin:auto;background:var(--gw-bg);color:var(--gw-text);border:1px solid var(--gw-line);border-radius:22px;padding:22px 18px 16px;text-align:center;box-shadow:0 12px 40px rgba(0,0,0,.55);transform:scale(.96);transition:transform .18s ease;-webkit-user-select:none;user-select:none}' +
    '.gw-on .gw-ask{transform:none}' +
    '.gw-ask *{box-sizing:border-box;font-family:inherit}' +
    '.gw-ask .gw-coin{display:block;margin:0 auto 12px}' +
    '.gw-ask-text{margin:0 0 18px;font-size:17px;font-weight:700;line-height:1.4}' +
    '.gw-ask-row{display:grid;grid-template-columns:1fr 1fr;gap:10px}' +
    '.gw-ask-row button{min-height:52px;border-radius:14px;font-size:17px;font-weight:800;cursor:pointer}' +
    '.gw-ask-no{border:1px solid var(--gw-line);background:transparent;color:var(--gw-text)}' +
    '.gw-ask-yes{border:0;background:var(--gw-accent);color:var(--gw-accent-ink)}' +
    '.gw-ask-row button:active{transform:scale(.97)}' +
    '.gw-consent-layer{position:fixed;inset:0;z-index:2147483600;background:#fff}' +
    '.gw-consent-layer iframe{display:block;width:100%;height:100%;border:0}' +
    '.gw-consent .gw-ask-text{font-size:22px;line-height:1.35;margin-bottom:10px}' +
    '.gw-consent-sub{margin:0 0 20px;font-size:17px;line-height:1.4;color:var(--gw-muted)}' +
    '.gw-consent .gw-ask-row{grid-template-columns:1fr}' +
    '.gw-consent .gw-ask-row button{min-height:60px;font-size:20px}' +
    '.gw-overlay.gw-center{align-items:center}' +
    '.gw-bal{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:12px}' +
    '.gw-card{border-radius:14px;background:var(--gw-surface);border:1px solid var(--gw-line);padding:10px 12px;min-width:0}' +
    '.gw-card.gw-blue{border-color:rgba(110,180,255,.75);background:linear-gradient(180deg,rgba(40,110,230,.22),rgba(20,60,160,.16))}' +
    '.gw-lbl{font-size:12px;font-weight:600;color:var(--gw-muted);margin:0 0 3px}' +
    '.gw-blue .gw-lbl{color:#9fd0ff}' +
    '.gw-val{display:flex;align-items:center;gap:6px;font-size:18px;font-weight:800;font-variant-numeric:tabular-nums;min-width:0}' +
    '.gw-val span{overflow:hidden;text-overflow:ellipsis;white-space:nowrap}' +
    '.gw-blue .gw-val{color:#cfe8ff}' +
    '.gw-val .gw-coin{flex:none}' +
    '.gw-tabs{display:grid;grid-template-columns:1fr 1fr;gap:4px;padding:4px;border-radius:14px;background:var(--gw-surface);border:1px solid var(--gw-line);margin-bottom:12px}' +
    '.gw-tab{min-height:48px;border:0;border-radius:10px;background:transparent;color:var(--gw-muted);font-size:14px;font-weight:700;line-height:1.15;cursor:pointer;padding:4px 6px;display:flex;flex-direction:column;align-items:center;justify-content:center}' +
    '.gw-tab small{display:none;font-size:11px;font-weight:600;margin-top:2px}' +
    '.gw-tab[aria-selected=true]{background:var(--gw-accent);color:var(--gw-accent-ink)}' +
    '.gw-tab:disabled{cursor:default;color:rgba(160,160,170,.75);background:rgba(128,128,140,.14)}' +
    '.gw-tab:disabled small{display:block}' +
    '.gw-quick{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:8px}' +
    '.gw-q{min-height:44px;border-radius:12px;border:1px solid var(--gw-line);background:var(--gw-surface);color:var(--gw-text);font-size:14px;font-weight:700;cursor:pointer;padding:0 2px}' +
    '.gw-q:not(:disabled):active,.gw-tab:not(:disabled):active,.gw-go:not(:disabled):active{transform:scale(.97)}' +
    '.gw-q[aria-pressed=true]{border-color:var(--gw-accent);color:var(--gw-accent)}' +
    '.gw-q:disabled{opacity:.35;cursor:default}' +
    '.gw-in{width:100%;height:52px;border-radius:14px;border:1px solid var(--gw-line);background:var(--gw-input);color:var(--gw-text);font-size:22px;font-weight:800;text-align:center;font-variant-numeric:tabular-nums;outline:none;-webkit-user-select:text;user-select:text}' +
    '.gw-in:focus{border-color:var(--gw-accent)}' +
    '.gw-in.gw-over{border-color:#f87171;color:#fecaca}' +
    '.gw-in:disabled{opacity:.45}' +
    '.gw-in::placeholder{color:var(--gw-muted);font-weight:600;font-size:17px}' +
    '.gw-note{font-size:13px;color:var(--gw-muted);text-align:center;margin:8px 0 12px;min-height:18px}' +
    '.gw-note.gw-warn{color:#fca5a5;font-weight:700}' +
    '.gw-msg{display:flex;flex-direction:column;justify-content:center;box-sizing:border-box;min-height:60px;visibility:hidden;border:1px solid transparent;border-radius:12px;padding:10px 12px;font-size:14px;font-weight:600;line-height:1.35;margin-bottom:10px}' +
    '.gw-msg.gw-err{visibility:visible;background:rgba(220,38,38,.16);color:#fecaca;border-color:rgba(248,113,113,.45)}' +
    '.gw-msg.gw-ok{visibility:visible;background:rgba(22,163,74,.16);color:#bbf7d0;border-color:rgba(74,222,128,.45)}' +
    '.gw-msg button{display:block;margin-top:8px;min-height:40px;width:100%;border-radius:10px;border:0;background:#fecaca;color:#450a0a;font-weight:800;font-size:14px;cursor:pointer}' +
    '.gw-go{width:100%;min-height:52px;border-radius:14px;border:0;background:var(--gw-accent);color:var(--gw-accent-ink);font-size:17px;font-weight:800;cursor:pointer}' +
    '.gw-go:disabled{cursor:default;background:rgba(128,128,140,.3);color:rgba(220,220,228,.7)}' +
    '.gw-back{width:100%;min-height:44px;margin-top:8px;border-radius:14px;border:1px solid var(--gw-line);background:transparent;color:var(--gw-text);font-size:15px;font-weight:700;cursor:pointer}'

  var cssDone = false
  function injectCss() {
    if (cssDone) return
    cssDone = true
    var st = document.createElement('style')
    st.setAttribute('data-game-wallet', '')
    st.textContent = CSS
    document.head.appendChild(st)
  }

  function el(tag, cls, text) {
    var n = document.createElement(tag)
    if (cls) n.className = cls
    if (text != null) n.textContent = text
    return n
  }

  // ---------------------------------------------------------------- pílula
  function createPill() {
    injectCss()
    var b = el('button', 'gw-pill')
    b.type = 'button'
    b.setAttribute('data-testid', 'wallet-button')
    b.insertAdjacentHTML('beforeend', coinSvg(26))
    var v = el('span', 'gw-pill-val')
    v.setAttribute('data-testid', 'gw-pill-value')
    var plus = el('span', 'gw-pill-plus', '+')
    plus.setAttribute('aria-hidden', 'true')
    b.appendChild(v)
    b.appendChild(plus)
    b.setValue = function (n) {
      var t = fmt(n)
      v.textContent = fmtShort(n) // nunca corta com reticências
      v.title = t
      b.setAttribute('data-value', String(Math.max(0, Math.floor(Number(n) || 0))))
      b.setAttribute('aria-label', NAME + ': ' + t + '. Trocar moedas')
    }
    b.setValue(state.gameCoins)
    b.addEventListener('click', function () {
      open('in')
    })
    pills.push(b)
    return b
  }

  // visual AZUL único em todos os jogos (o tema do jogo não muda o modal)
  function paintTheme(node) {
    var t = BLUE_THEME
    var vars = {
      '--gw-bg': t.bg || '#151821',
      '--gw-surface': t.surface || 'rgba(255,255,255,.07)',
      '--gw-input': t.input || 'rgba(0,0,0,.35)',
      '--gw-line': t.line || 'rgba(255,255,255,.14)',
      '--gw-text': t.text || '#f5f5f5',
      '--gw-muted': t.muted || 'rgba(245,245,245,.65)',
      '--gw-accent': t.accent || '#f8d26a',
      '--gw-accent-ink': t.accentInk || '#1a1206',
      '--gw-font': (cfg.theme && cfg.theme.font) || "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif",
    }
    Object.keys(vars).forEach(function (k) {
      node.style.setProperty(k, vars[k])
    })
  }

  // ---------------------------------------------------------------- aviso (Sim / Não)
  var askUi = null
  function ask(reason) {
    if (!state.enabled) return
    injectCss()
    if (isOpen()) return // a carteira já está aberta: não empilha
    var why = ASK[reason] ? reason : 'start'
    if (!askUi) {
      var ov = el('div', 'gw-overlay gw-center')
      ov.setAttribute('data-testid', 'gw-ask')
      paintTheme(ov)
      var box = el('div', 'gw-ask')
      box.setAttribute('role', 'alertdialog')
      box.setAttribute('aria-modal', 'true')
      box.setAttribute('aria-labelledby', 'gw-ask-text')
      box.insertAdjacentHTML('beforeend', coinSvg(56))
      var txt = el('p', 'gw-ask-text')
      txt.id = 'gw-ask-text'
      txt.setAttribute('data-testid', 'gw-ask-text')
      var row = el('div', 'gw-ask-row')
      var no = el('button', 'gw-ask-no', 'Não')
      no.type = 'button'
      no.setAttribute('data-testid', 'gw-ask-no')
      var yes = el('button', 'gw-ask-yes', 'Sim')
      yes.type = 'button'
      yes.setAttribute('data-testid', 'gw-ask-yes')
      row.appendChild(no)
      row.appendChild(yes)
      box.appendChild(txt)
      box.appendChild(row)
      ov.appendChild(box)
      askUi = { ov: ov, txt: txt }
      yes.addEventListener('click', function () {
        closeAsk()
        open('in')
      })
      no.addEventListener('click', function () {
        closeAsk()
        exitGame()
      })
    }
    askUi.txt.textContent = ASK[why]
    askUi.ov.setAttribute('data-reason', why)
    if (!askUi.ov.parentNode) document.body.appendChild(askUi.ov)
    requestAnimationFrame(function () {
      askUi.ov.classList.add('gw-on')
    })
  }
  function closeAsk() {
    if (!askUi || !askUi.ov.parentNode) return
    askUi.ov.classList.remove('gw-on')
    askUi.ov.parentNode.removeChild(askUi.ov)
  }

  // ---------------------------------------------------------------- Jogo Responsável (modal grande)
  // O servidor exigiu o termo/limite: em vez de só um aviso pequeno, abre um modal por cima de
  // tudo. "Sim, ativar" pede ao app pra abrir o Jogo Responsável; "Agora não" só fecha.
  var consentUi = null
  function consentAsk(code) {
    injectCss()
    var t = CONSENT_ASK[code] || CONSENT_ASK.consent_required
    if (!consentUi) {
      var ov = el('div', 'gw-overlay gw-center')
      ov.setAttribute('data-testid', 'gw-consent')
      paintTheme(ov)
      var box = el('div', 'gw-ask gw-consent')
      box.setAttribute('role', 'alertdialog')
      box.setAttribute('aria-modal', 'true')
      box.setAttribute('aria-labelledby', 'gw-consent-title')
      box.insertAdjacentHTML('beforeend', coinSvg(64))
      var title = el('p', 'gw-ask-text')
      title.id = 'gw-consent-title'
      var sub = el('p', 'gw-consent-sub')
      var row = el('div', 'gw-ask-row')
      var yes = el('button', 'gw-ask-yes', 'Sim, ativar')
      yes.type = 'button'
      yes.setAttribute('data-testid', 'gw-consent-yes')
      var no = el('button', 'gw-ask-no', 'Agora não')
      no.type = 'button'
      no.setAttribute('data-testid', 'gw-consent-no')
      row.appendChild(yes)
      row.appendChild(no)
      box.appendChild(title)
      box.appendChild(sub)
      box.appendChild(row)
      ov.appendChild(box)
      consentUi = { ov: ov, title: title, sub: sub }
      yes.addEventListener('click', function () {
        closeConsentAsk()
        openConsent()
      })
      no.addEventListener('click', closeConsentAsk)
    }
    consentUi.title.textContent = t[0]
    consentUi.sub.textContent = t[1]
    consentUi.ov.setAttribute('data-code', code)
    if (!consentUi.ov.parentNode) document.body.appendChild(consentUi.ov)
    requestAnimationFrame(function () {
      consentUi.ov.classList.add('gw-on')
    })
  }
  function closeConsentAsk() {
    if (!consentUi || !consentUi.ov.parentNode) return
    consentUi.ov.classList.remove('gw-on')
    consentUi.ov.parentNode.removeChild(consentUi.ov)
  }

  // ---------------------------------------------------------------- modal
  function build() {
    if (ui) return ui
    injectCss()

    var ov = el('div', 'gw-overlay')
    ov.setAttribute('data-testid', 'gw-overlay')
    paintTheme(ov)

    var sh = el('div', 'gw-sheet')

    sh.setAttribute('role', 'dialog')
    sh.setAttribute('aria-modal', 'true')
    sh.setAttribute('aria-labelledby', 'gw-title')

    var head = el('div', 'gw-head')
    var title = el('h2', 'gw-title', NAME)
    title.id = 'gw-title'
    var x = el('button', 'gw-x', '×')
    x.type = 'button'
    x.setAttribute('aria-label', 'Fechar')
    head.appendChild(title)
    head.appendChild(x)

    var intro = el('p', 'gw-intro')
    intro.setAttribute('data-testid', 'gw-intro')
    intro.hidden = true

    var bal = el('div', 'gw-bal')
    var c1 = el('div', 'gw-card')
    c1.appendChild(el('p', 'gw-lbl', 'Suas moedas'))
    var vC = el('div', 'gw-val')
    var vCoins = el('span')
    vCoins.setAttribute('data-testid', 'gw-coins')
    vC.appendChild(vCoins)
    c1.appendChild(vC)
    var c2 = el('div', 'gw-card gw-blue')
    c2.appendChild(el('p', 'gw-lbl', NAME))
    var vG = el('div', 'gw-val')
    vG.insertAdjacentHTML('beforeend', coinSvg(22))
    var vGame = el('span')
    vGame.setAttribute('data-testid', 'gw-game-coins')
    vG.appendChild(vGame)
    c2.appendChild(vG)
    bal.appendChild(c1)
    bal.appendChild(c2)

    var tabs = el('div', 'gw-tabs')
    tabs.setAttribute('role', 'tablist')
    function tab(label, sub, testid) {
      var b = el('button', 'gw-tab')
      b.type = 'button'
      b.setAttribute('role', 'tab')
      b.setAttribute('data-testid', testid)
      b.appendChild(el('span', '', label))
      b.appendChild(el('small', '', sub))
      tabs.appendChild(b)
      return b
    }
    var tIn = tab('Colocar em ' + NAME, emptyText('in'), 'gw-tab-in')
    var tOut = tab('Voltar para moedas', emptyText('out'), 'gw-tab-out')

    var quick = el('div', 'gw-quick')
    var qs = QUICK.map(function (v) {
      var b = el('button', 'gw-q', fmt(v))
      b.type = 'button'
      b.dataset.v = String(v)
      quick.appendChild(b)
      return b
    })
    var qAll = el('button', 'gw-q', 'Tudo')
    qAll.type = 'button'
    qAll.dataset.v = 'all'
    quick.appendChild(qAll)
    qs.push(qAll)

    var input = el('input', 'gw-in')
    input.type = 'text'
    input.inputMode = 'numeric'
    input.autocomplete = 'off'
    input.placeholder = 'Digite o valor'
    input.setAttribute('aria-label', 'Valor da troca')
    input.setAttribute('data-testid', 'gw-input')

    var note = el('p', 'gw-note')
    note.setAttribute('data-testid', 'gw-note')
    var msg = el('div', 'gw-msg')
    msg.setAttribute('role', 'status')
    msg.setAttribute('aria-live', 'polite')
    msg.setAttribute('data-testid', 'gw-msg')
    var go = el('button', 'gw-go')
    go.type = 'button'
    go.setAttribute('data-testid', 'gw-confirm')
    var back = el('button', 'gw-back', 'Voltar ao jogo')
    back.type = 'button'

    ;[head, intro, bal, tabs, quick, input, note, msg, go, back].forEach(function (n) {
      sh.appendChild(n)
    })
    ov.appendChild(sh)

    ui = { ov: ov, sh: sh, intro: intro, vCoins: vCoins, vGame: vGame, tIn: tIn, tOut: tOut, qs: qs, input: input, note: note, msg: msg, go: go }

    // eventos
    x.addEventListener('click', close)
    back.addEventListener('click', close)
    ov.addEventListener('click', function (e) {
      if (e.target === ov) close()
    })
    tIn.addEventListener('click', function () {
      if (!tIn.disabled) setMode('in')
    })
    tOut.addEventListener('click', function () {
      if (!tOut.disabled) setMode('out')
    })
    qs.forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.disabled || busy) return
        var v = b.dataset.v === 'all' ? maxFor(mode) : Number(b.dataset.v)
        input.value = v > 0 ? fmt(v) : ''
        clearMsg()
        paint()
      })
    })
    input.addEventListener('input', function () {
      var v = Math.min(parse(input.value), INT_MAX)
      input.value = v > 0 ? fmt(v) : ''
      clearMsg()
      paint()
    })
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') submit()
    })
    go.addEventListener('click', submit)
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && ov.parentNode) close()
    })
    return ui
  }

  function maxFor(m) {
    return Math.min(m === 'in' ? state.coins : state.gameCoins, INT_MAX)
  }

  function setMode(m) {
    mode = m
    if (ui) ui.input.value = ''
    clearMsg()
    paint()
  }

  function clearMsg() {
    if (!ui) return
    ui.msg.className = 'gw-msg'
    ui.msg.textContent = ''
  }
  function showMsg(kind, text, consentBtn) {
    if (!ui) return
    ui.msg.className = 'gw-msg ' + (kind === 'ok' ? 'gw-ok' : 'gw-err')
    ui.msg.textContent = text
    if (consentBtn) {
      var b = el('button', '', 'Abrir Jogo Responsável')
      b.type = 'button'
      b.addEventListener('click', openConsent)
      ui.msg.appendChild(b)
    }
  }

  function paint() {
    if (!ui) return
    ui.vCoins.textContent = fmt(state.coins)
    ui.vGame.textContent = fmt(state.gameCoins)
    var max = maxFor(mode)
    var empty = max <= 0
    ui.tIn.disabled = state.coins <= 0
    ui.tOut.disabled = state.gameCoins <= 0
    ui.tIn.setAttribute('aria-selected', String(mode === 'in'))
    ui.tOut.setAttribute('aria-selected', String(mode === 'out'))
    var v = parse(ui.input.value)
    var over = v > max
    ui.qs.forEach(function (b) {
      var bv = b.dataset.v === 'all' ? max : Number(b.dataset.v)
      b.disabled = busy || empty || bv > max
      b.setAttribute('aria-pressed', String(v > 0 && v === bv))
    })
    ui.input.disabled = busy || empty
    ui.input.classList.toggle('gw-over', over && !empty)
    if (empty) {
      ui.note.className = 'gw-note gw-warn'
      ui.note.textContent = emptyText(mode) + '.'
    } else if (over) {
      ui.note.className = 'gw-note gw-warn'
      ui.note.textContent = 'Você só tem ' + fmt(max) + (mode === 'in' ? ' moedas.' : ' ' + NAME + '.')
    } else {
      ui.note.className = 'gw-note'
      ui.note.textContent = '1 moeda = 1 Blue Coin, sem taxa.'
    }
    var amt = v > 0 && !over ? fmt(v) + ' ' : ''
    ui.go.textContent = busy
      ? 'Trocando…'
      : empty
        ? emptyText(mode)
        : mode === 'in'
          ? 'Colocar ' + amt + 'em ' + NAME
          : 'Voltar ' + amt + 'para moedas'
    ui.go.disabled = busy || empty || v <= 0 || over
  }

  function submit() {
    if (busy || !ui) return
    var amount = parse(ui.input.value)
    if (!(amount > 0) || amount > INT_MAX) return showMsg('err', errorText('invalid_value'))
    if (amount > maxFor(mode)) return paint()
    busy = true // trava na hora: toque duplo não troca 2 vezes
    clearMsg()
    paint()
    var m = mode
    gql(m === 'in' ? M_IN : M_OUT, { amount: amount, requestId: uuid() })
      .then(function (d) {
        busy = false
        ui.input.value = ''
        apply(d)
        showMsg(
          'ok',
          m === 'in'
            ? 'Pronto! ' + fmt(amount) + ' moedas viraram ' + NAME + '.'
            : 'Pronto! ' + fmt(amount) + ' ' + NAME + ' voltaram para as suas moedas.',
        )
        paint()
      })
      .catch(function (e) {
        busy = false
        var code = String((e && e.message) || 'error')
        if (code === 'Failed to fetch' || code === 'Load failed') code = 'network'
        var consent = isConsentError(code)
        showMsg('err', errorText(code), consent)
        if (consent) consentAsk(code)
        paint()
        refresh().catch(function () {})
      })
  }

  // reason: 'start' (entrou sem Blue Coins) | 'empty' (acabaram no jogo) → explicação
  // no topo e aba "Colocar" selecionada. Sem reason (pílula): só a carteira.
  function open(m, reason) {
    if (!state.enabled) return
    build()
    var why = INTRO[reason] ? reason : ''
    ui.intro.textContent = why ? INTRO[why] : ''
    ui.intro.hidden = !why
    ui.intro.setAttribute('data-reason', why)
    mode = why ? 'in' : m === 'out' ? 'out' : 'in'
    // aba sem saldo e a outra com saldo: abre na que dá pra usar
    if (!why && maxFor(mode) <= 0 && maxFor(mode === 'in' ? 'out' : 'in') > 0) mode = mode === 'in' ? 'out' : 'in'
    ui.input.value = ''
    clearMsg()
    paint()
    if (!ui.ov.parentNode) document.body.appendChild(ui.ov)
    // anima depois do primeiro quadro
    requestAnimationFrame(function () {
      ui.ov.classList.add('gw-on')
    })
    refresh().catch(function () {})
  }
  function close() {
    if (!ui || !ui.ov.parentNode) return
    ui.ov.classList.remove('gw-on')
    var ov = ui.ov
    setTimeout(function () {
      if (ov.parentNode) ov.parentNode.removeChild(ov)
    }, 180)
  }
  function isOpen() {
    return !!(ui && ui.ov.parentNode)
  }

  function init(opts) {
    cfg.apiUrl = opts.apiUrl
    cfg.token = opts.token
    cfg.theme = opts.theme || {}
    return refresh()
      .then(function (w) {
        if (w.enabled && w.gameCoins <= 0 && opts.autoOpenWhenEmpty !== false && !autoOpened) {
          autoOpened = true
          ask('start')
        }
        return w
      })
      .catch(function () {
        // sem a query (gateway antigo) ou sem rede: jogo segue como antes
        state.enabled = false
        return snapshot()
      })
  }

  root.GameWallet = {
    NAME: NAME,
    formatShort: fmtShort,
    init: init,
    open: open,
    ask: ask,
    exitGame: exitGame,
    close: close,
    isOpen: isOpen,
    refresh: refresh,
    createPill: createPill,
    coinSvg: coinSvg,
    openConsent: openConsent,
    consentAsk: consentAsk,
    isConsentError: isConsentError,
    onChange: function (fn) {
      listeners.push(fn)
      return function () {
        listeners = listeners.filter(function (x) {
          return x !== fn
        })
      }
    },
    get state() {
      return snapshot()
    },
  }
})(typeof window !== 'undefined' ? window : this)
